import React from 'react';
import { useNavigate } from 'react-router-dom';
import LegalEmail from './legal/LegalEmail';
import { LEGAL_LAST_UPDATED } from '../config/legal';
import { WEBSITE_URL, cameFromWebsite, legalHref } from './legal/siteReturn';

// Terms of Service (English). The Hebrew Terms are embedded in ConsentScreen.jsx (LEGAL_DOCS.terms) — same
// sections, same substance. Revision history and archived versions: LEGAL_REVISIONS.md.

function TermsOfService() {
  const navigate = useNavigate();
  const fromSite = cameFromWebsite();
  const privacyHref = legalHref('/privacy-en');

  return (
    <div className="min-h-screen bg-[#0f172a] text-white" dir="ltr">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#0f172a]/90 backdrop-blur-lg border-b border-white/5">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold">Terms of Service</h1>
          {fromSite ? (
            <a href={WEBSITE_URL} className="text-gray-400 hover:text-white transition">
              Back to RitmoIQ website
            </a>
          ) : (
            <button 
              onClick={() => navigate(-1)} 
              className="text-gray-400 hover:text-white transition"
            >
              ✕ Close
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="prose prose-invert max-w-none">
          
          <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
          <p className="text-gray-400 mb-8">Last Updated: {LEGAL_LAST_UPDATED.en}</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              Welcome to RitmoIQ ("the Service"), a personalized training coach application.
              RitmoIQ is a project privately operated by Lior Parovkevich and is not an incorporated company ("we," "us," or "our").
              By accessing or using our Service, 
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to 
              these Terms, please do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">2. Service Description</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              RitmoIQ provides AI-powered running coaching services, including:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Personalized weekly training plans</li>
              <li>Activity analysis and feedback</li>
              <li>Race preparation guidance</li>
              <li>Garmin Connect integration through Stridee (closed pilot, for approved users)</li>
              <li>Coach-athlete management tools</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">3. Use of AI Technology</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              RitmoIQ uses AI services from OpenAI (OpenAI API) and Google (Gemini API) to provide AI-generated personalized coaching,
              workout analysis and feedback. Important clarifications regarding our AI usage:
            </p>
            <div className="bg-[#1e293b] rounded-xl p-4 border border-white/10 mb-4">
              <h3 className="text-white font-semibold mb-2">✅ How We Use AI:</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>To analyze YOUR OWN activity data and provide personalized advice</li>
                <li>To generate customized training plans based on your profile and goals</li>
                <li>To answer your questions about running, training, and fitness</li>
                <li>Real-time coaching conversations visible only to you (and your coach, if applicable)</li>
              </ul>
            </div>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
              <h3 className="text-red-400 font-semibold mb-2">❌ What We Do NOT Do:</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                <li>We do NOT use your data to train our own AI models</li>
                <li>We do NOT sell your data, or share it with third parties for marketing or advertising purposes</li>
                <li>We do NOT use your data for purposes other than providing and improving the Service, as described in our Privacy Policy</li>
              </ul>
            </div>
            <p className="text-gray-300 leading-relaxed mt-4">
              For details about what information is processed, including the information sent to the
              AI providers, please see our{' '}
              <a href={privacyHref} className="text-cyan-400 hover:underline">Privacy Policy</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">4. Third-Party Integrations</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              The Garmin Connect integration through Stridee depends on third-party services and is
              subject to those providers' own terms, availability and API access. Connecting it is optional.
            </p>

            <h3 className="text-white font-semibold mt-4 mb-2">4.1 Strava (Integration Discontinued)</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              The Strava integration was discontinued on October 21, 2026, and Strava can no longer be
              connected to the Service. We no longer receive new information from Strava. RitmoIQ does not
              use information received from Strava to train its own AI models and, based on our current
              implementation, does not send it to the AI providers. For information previously received and how to
              remove RitmoIQ's access on Strava's My Apps page, see Section 8 of our{' '}
              <a href={privacyHref} className="text-cyan-400 hover:underline">Privacy Policy</a>.
            </p>

            <h3 className="text-white font-semibold mt-6 mb-2">4.2 Garmin Connect Integration (Closed Pilot)</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              The Garmin Connect integration is available through Stridee to users approved for the closed
              pilot, and connecting it is optional. When you connect Garmin:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
              <li>Your completed running activities are received and shown in the app</li>
              <li>If you also accepted the AI clause, the AI Coach analyzes them using the AI providers (OpenAI and Google Gemini) and uses them for recommendations and adjustments to your training plan</li>
              <li>You can withdraw the AI analysis consent at any time from your profile</li>
              <li>You can disconnect Garmin at any time from your profile</li>
            </ul>
            <p className="text-gray-300 leading-relaxed">
              For what is received, kept and sent, see Section 9 of our Privacy Policy. Sending workouts
              to Garmin is not part of the pilot.
            </p>

            <h3 className="text-white font-semibold mt-6 mb-2">4.3 AI Service Providers (OpenAI API and Google Gemini API)</h3>
            <p className="text-gray-300 leading-relaxed">
              We use the paid API services of OpenAI and Google to generate AI coaching responses, workout
              analysis and feedback. OpenAI's processing of information sent to the OpenAI API is subject
              to the{' '}
              <a href="https://openai.com/policies/services-agreement/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">OpenAI API terms</a>, and
              Google's processing of information sent to the Gemini API is subject to the{' '}
              <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">Gemini API Terms of Service</a>.
              RitmoIQ does not use your data to train its own AI models. See our{' '}
              <a href={privacyHref} className="text-cyan-400 hover:underline">Privacy Policy</a> for
              details about data processing.
            </p>

            <h3 className="text-white font-semibold mt-6 mb-2">4.4 Availability of Third-Party Integrations</h3>
            <p className="text-gray-300 leading-relaxed">
              RitmoIQ cannot guarantee the continued availability of any third-party integration.
              If a provider changes, limits or withdraws access to its services or API, the related
              functionality in RitmoIQ may be changed, limited or discontinued.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">5. Data Storage and Security</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Your data is stored securely on our servers with the following protections:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure database storage with access controls</li>
              <li>Regular security audits and updates</li>
              <li>No sharing with unauthorized third parties</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">6. User Responsibilities</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              By using the Service, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide accurate information about your fitness level and health</li>
              <li>Seek medical advice before starting a training program where appropriate, and in particular if you have a medical condition, injury, persistent pain, concerning symptoms, or any doubt about your ability to exercise</li>
              <li>Use the AI coaching advice as guidance, not medical advice</li>
              <li>Not attempt to manipulate or misuse the AI system</li>
              <li>Respect the intellectual property rights of the Service</li>
              <li>Stop exercising and seek appropriate medical advice if you experience pain, unusual discomfort, dizziness, shortness of breath or any other concerning symptoms</li>
              <li>Use the Service only for lawful purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">7. Disclaimer and Limitation of Liability</h2>

            <h3 className="text-white font-semibold mt-4 mb-2">7.1 Not Medical Advice</h3>
            <p className="text-gray-300 leading-relaxed">
              The Service provides AI-generated coaching advice for informational purposes only. It is NOT a
              substitute for professional medical advice, diagnosis, or treatment, and it is not suitable for
              medical emergencies. In a medical emergency, contact emergency services immediately. Where
              appropriate, the Service does not replace guidance from a qualified or certified coach. We recommend consulting a qualified healthcare provider before beginning a training program or making changes to your training. This is particularly important if you have a medical condition, injury, persistent pain, concerning symptoms, or any doubt about your ability to exercise.
            </p>

            <h3 className="text-white font-semibold mt-6 mb-2">7.2 Service Provided "As Is"</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              The Service is provided on an "as is" and "as available" basis. To the extent permitted by
              applicable law, we do not guarantee that:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>the Service will be available without interruption or errors</li>
              <li>AI-generated recommendations, plans or analyses will be accurate, complete or error-free</li>
              <li>using the Service will lead to any particular training, race or health outcome</li>
              <li>the Service will be suitable for every user</li>
            </ul>

            <h3 className="text-white font-semibold mt-6 mb-2">7.3 Limitation of Liability</h3>
            <p className="text-gray-300 leading-relaxed">
              To the extent permitted by applicable law, we are not liable for injuries or health issues
              resulting from following the advice provided by the Service. To the extent permitted by
              applicable law, RitmoIQ will not be liable for indirect, incidental, special or consequential
              damages, or for loss of data, arising from your use of the Service. Nothing in these Terms
              excludes or limits any liability that cannot be excluded or limited under applicable law, or
              any rights you have under mandatory consumer protection law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">8. Pilot Access and Future Paid Services</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              RitmoIQ is currently operating as a free, closed pilot. Access to the Service may
              require approval by RitmoIQ and may be limited or withdrawn during the pilot.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              No paid subscription is currently offered through the Service, and users are not
              currently charged subscription fees.
            </p>
            <p className="text-gray-300 leading-relaxed">
              RitmoIQ may introduce paid plans or subscription services in the future. If paid
              services are introduced, applicable pricing, billing, renewal, cancellation and refund
              terms will be presented to users before they purchase or subscribe to such services,
              and these Terms may be updated accordingly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">9. Termination</h2>
            <p className="text-gray-300 leading-relaxed">
              You may delete your account at any time in the app (Profile → Delete Account) or by
              contacting us at <LegalEmail className="text-cyan-400 hover:underline" />. Once deleted, your account
              cannot be restored. Deletion covers your RitmoIQ account data and your login account, as
              described in our <a href={privacyHref} className="text-cyan-400 hover:underline">Privacy Policy</a>,
              including how backups are handled. Removing RitmoIQ's access in Strava, or disconnecting
              Garmin, does not by itself delete information we already hold. We reserve the right to
              suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update these Terms from time to time. We will notify users of significant 
              changes via email or in-app notification. Continued use of the Service after 
              changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">11. Contact Information</h2>
            <p className="text-gray-300 leading-relaxed">
              For questions about these Terms or the Service, please contact us at:
            </p>
            <p className="text-gray-300 mt-2">
              Email: <LegalEmail className="text-cyan-400 hover:underline" />
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-cyan-400 mb-4">12. Governing Law and Jurisdiction</h2>
            <p className="text-gray-300 leading-relaxed">
              These Terms are governed by the laws of the State of Israel. Any dispute relating to these
              Terms or the Service will be heard by the competent courts in Tel Aviv-Jaffa, Israel.
            </p>
          </section>

          <section className="mb-8 p-4 bg-[#1e293b] rounded-xl border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-4">Summary of Key Points</h2>
            <ul className="space-y-2 text-gray-300">
              <li>✅ Your data is used to provide and improve YOUR coaching service, as described in our Privacy Policy</li>
              <li>🤖 AI coaching is generated using the OpenAI API and the Google Gemini API</li>
              <li>🔗 Your training source is Garmin Connect through Stridee (closed pilot, optional); the Strava integration has been discontinued. Integrations depend on third-party availability</li>
              <li>❌ We do NOT train our own AI models with your data</li>
              <li>❌ We do NOT sell your data or share it for marketing or advertising</li>
              <li>🔒 You can delete your account at any time from Profile → Delete Account</li>
            </ul>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 mt-8">
        <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
          © 2026 RitmoIQ. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
